import React, { useState, useEffect, useRef } from "react";
import "./Forms.css";
import "./FormResponsive.css";
import { Snackbar, Alert as MuiAlert } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../axiosinstance";

export default function FamilyGroupForm({ isSidebarExpanded }) {
  const { id } = useParams(); // present when editing
  const isEditMode = Boolean(id);
  const navigate = useNavigate();

  const [familyName, setFamilyName] = useState("");
  const [contactName, setContactName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const firstField = useRef();

  // ─── Focus first field on mount ──────────────────────────────────────────
  useEffect(() => {
    firstField.current?.focus();
  }, []);

  // ─── Prefill when editing ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode) return;

    const fetchFamilyGroup = async () => {
      try {
        const response = await axiosInstance.get(`/api/family_group/${id}`);
        const data = response.data?.data || response.data;
        setFamilyName(data.family_name || "");
        setContactName(data.contact_name || "");
        setPhoneNumber((data.phone_number || "").toString());
        setEmail(data.email || "");
      } catch (error) {
        console.error("Error fetching family group:", error);
        setSnackbarSeverity("error");
        setSnackbarMessage("Failed to load family group details");
        setSnackbarOpen(true);
      }
    };

    fetchFamilyGroup();
  }, [id, isEditMode]);

  // ─── Validation ───────────────────────────────────────────────────────────
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!familyName.trim()) {
      newErrors.familyName = "Family Name is required";
      isValid = false;
    } else if (familyName.length > 100) {
      newErrors.familyName = "Family Name must not exceed 100 characters";
      isValid = false;
    }

    if (!contactName.trim()) {
      newErrors.contactName = "Contact Name is required";
      isValid = false;
    } else if (contactName.length > 100) {
      newErrors.contactName = "Contact Name must not exceed 100 characters";
      isValid = false;
    }

    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone Number is required";
      isValid = false;
    } else if (!/^\d{10}$/.test(phoneNumber.trim())) {
      newErrors.phoneNumber = "Phone Number must be exactly 10 digits";
      isValid = false;
    }

    if (!email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!emailRegex.test(email.trim())) {
      newErrors.email = "Enter a valid email address";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || loading || isSaved) return;

    setLoading(true);

    const payload = {
      family_name: familyName.trim(),
      contact_name: contactName.trim(),
      phone_number: phoneNumber.trim(),
      email: email.trim(),
    };

    try {
      let response;
      if (isEditMode) {
        response = await axiosInstance.put(`/api/family_group/${id}`, payload);
      } else {
        response = await axiosInstance.post("/api/create_family_group", payload);
      }

      setSnackbarSeverity("success");
      setSnackbarMessage(
        response.data?.message ||
          (isEditMode ? "Family Group updated successfully!" : "Family Group created successfully!")
      );
      setSnackbarOpen(true);
      setIsSaved(true);

      setTimeout(() => {
        navigate("/family-groups");
      }, 2000);
    } catch (error) {
      console.error("Error saving family group:", error);
      setSnackbarSeverity("error");
      setSnackbarMessage(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to save Family Group. Please try again."
      );
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // ─── Clear error on change helper ────────────────────────────────────────
  const clearError = (field) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <div
      className={`table-container family-group-form-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="table-container-content form-main-body family-group-form-content">
        <div className="family-group-page-heading">
          <h1 className="form-title">
            {isEditMode ? "Edit Family Group" : "New Family Group"}
          </h1>
          <p className="family-group-page-subtitle">
            Create a family group with its primary contact details.
          </p>
        </div>

        <div className="family-group-form-card">
          <div className="form-body-client-management family-group-form-body">
          <form className="form-box family-group-form-box" onSubmit={handleSubmit}>
            <div className="form-body-container family-group-form-sections">
              <section className="family-group-section family-group-section-last">
                <div className="family-group-section-header">
                  <div>
                    <h2 className="family-group-section-title">
                      Family Group Details
                    </h2>
                    <p className="family-group-section-description">
                      Add the family name and primary contact information.
                    </p>
                  </div>
                </div>

              {/* Family Name */}
              <div className="form-group">
                <label className="form-label">
                  Family Name <span className="required">*</span>
                </label>
                <div className="form-inputs">
                  <input
                    ref={firstField}
                    type="text"
                    className="input-field"
                    placeholder="Enter Family Name"
                    value={familyName}
                    maxLength={100}
                    onChange={(e) => {
                      setFamilyName(e.target.value);
                      clearError("familyName");
                    }}
                    required
                  />
                  {errors.familyName && (
                    <p className="error-message">{errors.familyName}</p>
                  )}
                </div>
              </div>

              {/* Contact Name */}
              <div className="form-group">
                <label className="form-label">
                  Contact Name <span className="required">*</span>
                </label>
                <div className="form-inputs">
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Enter Contact Name"
                    value={contactName}
                    maxLength={100}
                    onChange={(e) => {
                      setContactName(e.target.value);
                      clearError("contactName");
                    }}
                    required
                  />
                  {errors.contactName && (
                    <p className="error-message">{errors.contactName}</p>
                  )}
                </div>
              </div>

              {/* Phone Number */}
              <div className="form-group">
                <label className="form-label">
                  Phone Number <span className="required">*</span>
                </label>
                <div className="form-inputs">
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Enter Phone Number"
                    value={phoneNumber}
                    maxLength={10}
                    inputMode="numeric"
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setPhoneNumber(val);
                      clearError("phoneNumber");
                    }}
                    required
                  />
                  {errors.phoneNumber && (
                    <p className="error-message">{errors.phoneNumber}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="form-group">
                <label className="form-label">
                  Email <span className="required">*</span>
                </label>
                <div className="form-inputs">
                  <input
                    type="email"
                    className="input-field"
                    placeholder="Enter Email Address"
                    value={email}
                    maxLength={100}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      clearError("email");
                    }}
                    required
                  />
                  {errors.email && (
                    <p className="error-message">{errors.email}</p>
                  )}
                </div>
              </div>

            </section>
            </div>
          </form>
        </div>

      {/* ── Footer ── */}
      <div className="form-footer">
        <div className="button-group">
          <button
            type="button"
            className="btn btn-cancel"
            onClick={() => window.history.back()}
            disabled={loading}
          >
            CANCEL
          </button>
          <button
            type="submit"
            className="btn btn-save"
            onClick={handleSubmit}
            disabled={loading || isSaved}
          >
            {loading ? "Saving..." : isSaved ? "Saved" : "SAVE"}
          </button>
        </div>
      </div>

      {/* ── Snackbar ── */}
      </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ transform: "translateY(40px)", marginLeft: "15px" }}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}
